import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  DisplayUserEmail,
  FolderInvitation,
  FolderMember,
  FolderMemberAccess,
  FolderTenantAccessLevel,
} from '@nusign/picflow-backend-types';
import { catchError, delay, forkJoin, map, Observable, of, retry, switchMap, take, tap, throwError } from 'rxjs';
import { standardMemoryCacheExpiry } from '../consts';
import { IGNORE_AUTH_ERRORS } from '../network-interceptor';
import { PICFLOW_CONFIG } from '../picflow-config';
import { GetAllService } from './get-all.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { DisplayUserEmailDto } from './visitor.service';
import { WafHttpService } from './waf-http.service';

@Injectable({
  providedIn: 'root',
})
export class FolderMemberService {
  private config = inject(PICFLOW_CONFIG);
  private http = inject(HttpClient);
  private loader = inject(ObservableDataLoaderService);
  private getAllService = inject(GetAllService);
  private wafHttpService = inject(WafHttpService);

  public getFolderMembers(folderId: string) {
    return this.loader.get(`folder_members_${folderId}`, () =>
      this.getAllService.getAll<FolderMember & { user: DisplayUserEmail }>(
        `${this.config.apiEndpoint}/folder_members?folder=${folderId}&expand[]=data.user`
      )
    );
  }

  public getFolderInvitations(folderId: string) {
    return this.loader.get(`folder_invitations_${folderId}`, () =>
      this.getAllService.getAll<FolderInvitation>(
        `${this.config.apiEndpoint}/folder_invitations?folder=${folderId}&expand[]=data.user`,
        new HttpContext().set(IGNORE_AUTH_ERRORS, true)
      )
    );
  }

  public getFolderMembersForUser(userId: string): Observable<FolderMember[]> {
    return this.getAllFolderMembers(false).pipe(map(members => members.filter(m => m.user['id'] === userId)));
  }

  private waitTillMemberIsAdded(folderId: string, email: string) {
    return of(undefined).pipe(
      switchMap(() => {
        return forkJoin([
          this.loader
            .forceDataRefresh<(FolderMember & { user: DisplayUserEmail })[]>(`folder_members_${folderId}`)
            .pipe(take(1)),
          this.loader.forceDataRefresh<FolderInvitation[]>(`folder_invitations_${folderId}`).pipe(take(1)),
        ]).pipe(
          switchMap(([members, invites]) => {
            if (members.find(m => m.user.email === email)) return of(true);
            if (invites.find(i => i.email === email)) return of(true);
            return throwError(() => 'Member not added');
          })
        );
      }),
      retry({ count: 30, delay: 1000 }),
      catchError(() => of(false))
    );
  }

  public inviteMember(
    email: string,
    folderId: string,
    accessLevel: FolderTenantAccessLevel,
    notify: boolean,
    message: string
  ) {
    const params = {
      email,
      folder: folderId,
      access: accessLevel,
      message: message || undefined,
      notify: notify,
    };
    return this.wafHttpService.post(`${this.config.apiEndpoint}/folder_invitations`, params).pipe(
      delay(500), // waiting for the backend to auto-accept the invite
      switchMap(() => this.waitTillMemberIsAdded(folderId, email)),
      tap(memberAdded => {
        if (!memberAdded) return;
        this.loader.forceDataRefresh('folder_members');
        this.loader.forceDataRefresh('folder_invitations');
      })
    );
  }

  public updateMember(memberId: string, accessLevel: FolderMemberAccess) {
    const params = {
      access: accessLevel,
    };
    return this.http.patch(`${this.config.apiEndpoint}/folder_members/${memberId}`, params).pipe(
      tap(() => {
        this.loader.updateFuzzy<FolderMember[]>('folder_members', members => {
          const member = members.find(m => m.id === memberId);
          if (!member) return members;
          member.access = accessLevel;
          return members;
        });
      })
    );
  }

  public updateInvite(inviteId: string, accessLevel: FolderMemberAccess) {
    const params = {
      access: accessLevel,
    };
    return this.http.patch(`${this.config.apiEndpoint}/folder_invitations/${inviteId}`, params).pipe(
      tap(() => {
        this.loader.updateFuzzy<FolderInvitation[]>('folder_invitations', invites => {
          const invite = invites.find(i => i.id === inviteId);
          if (!invite) return invites;
          invite.access = accessLevel;
          return invites;
        });
      })
    );
  }

  public removeMember(memberId: string) {
    return this.http.delete(`${this.config.apiEndpoint}/folder_members/${memberId}`).pipe(
      tap(() => {
        this.loader.updateFuzzy<FolderMember[]>('folder_members', members =>
          (members || []).filter(m => m.id !== memberId)
        );
      })
    );
  }

  public removeInvite(inviteId: string) {
    return this.http.delete(`${this.config.apiEndpoint}/folder_invitations/${inviteId}`).pipe(
      tap(() => {
        this.loader.updateFuzzy<FolderInvitation[]>('folder_invitations', invites =>
          (invites || []).filter(m => m.id !== inviteId)
        );
      })
    );
  }

  public getAllFolderMembers(unique: boolean): Observable<FolderMember[]> {
    return this.loader
      .get(
        'folder_members',
        () =>
          this.getAllService.getAll<FolderMember>(
            `${this.config.apiEndpoint}/folder_members?expand[]=data.user`,
            new HttpContext().set(IGNORE_AUTH_ERRORS, true)
          ),
        standardMemoryCacheExpiry,
        false
      )
      .pipe(
        catchError(err => {
          if (!(err instanceof HttpErrorResponse)) throw err;
          if (err.status !== 401 && err.status !== 403) throw err;
          return of([]);
        }),
        map(members => (unique ? this.makeMembersUnique(members) : members))
      );
  }

  makeMembersUnique(members: FolderMember[]) {
    const uniqueMembers: FolderMember[] = [];
    members.forEach(member => {
      const user = member.user as DisplayUserEmailDto;
      if (!uniqueMembers.find(m => m.user['id'] === user.id)) {
        uniqueMembers.push(member);
      }
    });
    return uniqueMembers;
  }
}
